import { useEffect, useState } from 'react';
import client from '@api';
import {
  peekSharedQuery,
  peekSharedValue,
  sharedQuery,
  sharedQueryKey,
  storeSharedQuery,
} from '@utils/sharedQuery';

import { propertyErrorMessage } from '../errors';
import {
  propertyGrowthQuery,
  propertyInsightsOfKindsQuery,
  propertyInsightsQuery,
  propertyInsightsSummaryQuery,
} from './api';
import type {
  AccountGrowth,
  InsightKind,
  InsightSnapshot,
  PropertyInsights,
} from './types';

const byKind = (snapshots: InsightSnapshot[]): PropertyInsights => {
  const find = (kind: InsightSnapshot['kind']) =>
    snapshots.find((s) => s.kind === kind);
  return {
    comments: find('audience_comments') as PropertyInsights['comments'],
    formats: find('market_formats') as PropertyInsights['formats'],
    topics: find('market_topics') as PropertyInsights['topics'],
    overlap: find('audience_overlap') as PropertyInsights['overlap'],
    interests: find('audience_interests') as PropertyInsights['interests'],
    commercial: find('commercial') as PropertyInsights['commercial'],
    history: find('channel_history') as PropertyInsights['history'],
    tagged: find('tagged_posts') as PropertyInsights['tagged'],
    opportunities: find('opportunities') as PropertyInsights['opportunities'],
    series: find('series_suggestions') as PropertyInsights['series'],
    seriesComments: find(
      'series_comments',
    ) as PropertyInsights['seriesComments'],
    sentiment: find('audience_sentiment') as PropertyInsights['sentiment'],
    trending: find('market_trending') as PropertyInsights['trending'],
    competitors: find('competitors') as PropertyInsights['competitors'],
  };
};

/** Adds `list` to `insights` without clearing kinds loaded by another request. */
const withSnapshots = (
  insights: PropertyInsights,
  list: InsightSnapshot[],
): PropertyInsights => ({
  ...insights,
  ...(Object.fromEntries(
    Object.entries(byKind(list)).filter(([, snapshot]) => snapshot),
  ) as PropertyInsights),
});

export const ALL_INSIGHT_KINDS: InsightKind[] = [
  'audience_comments',
  'market_formats',
  'market_topics',
  'audience_overlap',
  'audience_interests',
  'commercial',
  'channel_history',
  'tagged_posts',
  'opportunities',
  'series_suggestions',
  'audience_sentiment',
  'market_trending',
  'competitors',
];

/** The research snapshots the Market page reads; comments and tagged posts are the heavy ones it skips. */
export const MARKET_KINDS: InsightKind[] = [
  'market_formats',
  'market_topics',
  'commercial',
  'audience_overlap',
  'market_trending',
  'competitors',
];

/* Snapshots are rebuilt by research runs (hours apart), and any GraphQL
 * mutation clears the shared results, so switching between property tabs and
 * workspace pages can reuse what is already here instead of re-downloading
 * it. Growth moves daily. */
const INSIGHTS_MAX_AGE_MS = 2 * 60_000;
const GROWTH_MAX_AGE_MS = 60_000;

const kindKey = (propertyId: string, kind: InsightKind) =>
  sharedQueryKey('PropertyInsightsKind', { propertyId, kind });
const growthKey = (propertyId: string) =>
  sharedQueryKey('PropertyGrowth', { propertyId, days: 90 });

/**
 * Kinds loaded without `payload.items`: screens page those on demand
 * (`useInsightItems`). Never `opportunities`, whose items Home renders.
 */
export const SUMMARY_KINDS: InsightKind[] = ['audience_comments'];

type SnapshotsResult = { propertyInsights: InsightSnapshot[] };

/**
 * Loads only `kinds`, the `SUMMARY_KINDS` among them without their items.
 * A backend without `summaryKinds` (or `kinds`) rejects the argument, so each
 * attempt stays quiet and falls back to the previous query shape: full
 * snapshots of `kinds`, then every snapshot, filtered here. Readers of a
 * summary kind handle both shapes.
 */
const fetchSnapshots = (
  propertyId: string,
  kinds: InsightKind[],
  silent = false,
): Promise<InsightSnapshot[]> => {
  const quiet = { suppressGlobalErrorToast: true };
  const upper = (list: InsightKind[]) => list.map((k) => k.toUpperCase());
  const summaryKinds = kinds.filter((k) => SUMMARY_KINDS.includes(k));
  const ofKinds = () =>
    client().query<SnapshotsResult>({
      query: propertyInsightsOfKindsQuery,
      variables: { propertyId, kinds: upper(kinds) },
      fetchPolicy: 'network-only',
      context: quiet,
    });
  const first = summaryKinds.length
    ? client()
        .query<SnapshotsResult>({
          query: propertyInsightsSummaryQuery,
          variables: {
            propertyId,
            kinds: upper(kinds),
            summaryKinds: upper(summaryKinds),
          },
          fetchPolicy: 'network-only',
          context: quiet,
        })
        .catch(ofKinds)
    : ofKinds();
  return first
    .then((r) => r.data.propertyInsights || [])
    .catch(() =>
      client()
        .query<SnapshotsResult>({
          query: propertyInsightsQuery,
          variables: { propertyId },
          fetchPolicy: 'network-only',
          context: silent ? quiet : undefined,
        })
        .then((r) => r.data.propertyInsights || []),
    )
    .then((all) => all.filter((s) => kinds.includes(s.kind)));
};

/**
 * One request for the kinds nobody has loaded or is loading yet; kinds already
 * in flight (another hook, a prefetch) join that request instead.
 */
const loadKinds = (
  propertyId: string,
  kinds: InsightKind[],
  silent = false,
): Promise<InsightSnapshot[]> => {
  const fresh = { maxAgeMs: INSIGHTS_MAX_AGE_MS };
  const missing = kinds.filter(
    (kind) => !peekSharedQuery(kindKey(propertyId, kind), fresh),
  );
  if (missing.length) {
    const batch = fetchSnapshots(propertyId, missing, silent);
    missing.forEach((kind) =>
      storeSharedQuery(
        kindKey(propertyId, kind),
        batch.then((list) => list.find((s) => s.kind === kind) || null),
        INSIGHTS_MAX_AGE_MS,
      ),
    );
  }
  return Promise.all(
    kinds.map(
      (kind) =>
        peekSharedQuery<InsightSnapshot | null>(
          kindKey(propertyId, kind),
          fresh,
        ) || Promise.resolve(null),
    ),
  ).then((list) => list.filter((s): s is InsightSnapshot => !!s));
};

const loadGrowth = (propertyId: string): Promise<AccountGrowth[]> =>
  sharedQuery(
    growthKey(propertyId),
    () =>
      client()
        .query<{ propertyGrowth: AccountGrowth[] }>({
          query: propertyGrowthQuery,
          variables: { propertyId, days: 90 },
          fetchPolicy: 'network-only',
        })
        .then((r) => r.data.propertyGrowth || []),
    { maxAgeMs: GROWTH_MAX_AGE_MS },
  );

export type PropertyHomeOptions = {
  /** Kinds the page can render without: loading does not wait for them and
   * they fill in when they land (see `pendingKinds`). */
  deferred?: InsightKind[];
  /** Account growth; only the overview reads it. Default true. */
  growth?: boolean;
};

/**
 * Starts the requests `usePropertyHomeData` would make, so a page can fire
 * them as soon as it knows the property instead of when the component that
 * renders them mounts. The hook then joins the requests in flight.
 */
export const prefetchPropertyHomeData = (
  propertyId: string,
  kinds: InsightKind[] = ALL_INSIGHT_KINDS,
  { deferred = [], growth = true }: PropertyHomeOptions = {},
): void => {
  if (!propertyId) return;
  const quiet = (p: Promise<unknown>) => p.catch(() => undefined);
  if (growth) void quiet(loadGrowth(propertyId));
  void quiet(
    loadKinds(
      propertyId,
      kinds.filter((k) => !deferred.includes(k)),
      true,
    ),
  );
  const later = kinds.filter((k) => deferred.includes(k));
  if (later.length) void quiet(loadKinds(propertyId, later, true));
};

export type PropertyHomeData = {
  /** The property these snapshots belong to; paged items are loaded by it. */
  propertyId?: string;
  loading: boolean;
  error: string;
  growth: AccountGrowth[];
  insights: PropertyInsights;
  /** Deferred kinds still loading; their sections should show a loader. */
  pendingKinds?: InsightKind[];
};

/** What is already loaded for these kinds, so a remount renders at once. */
const cachedState = (
  propertyId: string,
  kinds: InsightKind[],
  deferred: InsightKind[],
  withGrowth: boolean,
): PropertyHomeData => {
  const fresh = { maxAgeMs: INSIGHTS_MAX_AGE_MS };
  const loaded: InsightSnapshot[] = [];
  const missing: InsightKind[] = [];
  kinds.forEach((kind) => {
    const hit = peekSharedValue<InsightSnapshot | null>(
      kindKey(propertyId, kind),
      fresh,
    );
    if (!hit) missing.push(kind);
    else if (hit.value) loaded.push(hit.value);
  });
  const growth = withGrowth
    ? peekSharedValue<AccountGrowth[]>(growthKey(propertyId), {
        maxAgeMs: GROWTH_MAX_AGE_MS,
      })
    : { value: [] };
  return {
    propertyId,
    loading: !growth || missing.some((k) => !deferred.includes(k)),
    error: '',
    growth: growth?.value || [],
    insights: byKind(loaded),
    pendingKinds: missing.filter((k) => deferred.includes(k)),
  };
};

/** Growth and research snapshots load independently so one failure never blanks the other. */
export function usePropertyHomeData(
  propertyId: string,
  kinds?: InsightKind[],
  { deferred, growth: withGrowth = true }: PropertyHomeOptions = {},
): PropertyHomeData {
  const kindsKey = (kinds || ALL_INSIGHT_KINDS).join(',');
  const deferredKey = (deferred || []).join(',');
  const [state, setState] = useState<PropertyHomeData>(() =>
    cachedState(
      propertyId,
      kinds || ALL_INSIGHT_KINDS,
      deferred || [],
      withGrowth,
    ),
  );

  useEffect(() => {
    let live = true;
    const wanted = (kindsKey ? kindsKey.split(',') : []) as InsightKind[];
    const later = deferredKey
      ? wanted.filter((k) => deferredKey.split(',').includes(k))
      : [];
    const now = wanted.filter((k) => !later.includes(k));
    setState(cachedState(propertyId, wanted, later, withGrowth));

    const growth = withGrowth
      ? loadGrowth(propertyId)
      : Promise.resolve<AccountGrowth[]>([]);
    const insights = loadKinds(propertyId, now);
    void Promise.allSettled([growth, insights]).then(([g, i]) => {
      if (!live) return;
      const failed = [g, i].find((r) => r.status === 'rejected') as
        | PromiseRejectedResult
        | undefined;
      setState((previous) => ({
        ...previous,
        loading: false,
        error: failed
          ? propertyErrorMessage(
              failed.reason,
              'Some overview data could not load.',
            )
          : previous.error,
        growth: g.status === 'fulfilled' ? g.value : [],
        insights:
          i.status === 'fulfilled'
            ? withSnapshots(previous.insights, i.value)
            : previous.insights,
      }));
    });
    if (later.length) {
      void loadKinds(propertyId, later)
        .then((list) => ({ list, error: '' }))
        .catch((error: unknown) => ({
          list: [] as InsightSnapshot[],
          error: propertyErrorMessage(
            error,
            'Some overview data could not load.',
          ),
        }))
        .then(({ list, error }) => {
          if (!live) return;
          setState((previous) => ({
            ...previous,
            error: previous.error || error,
            insights: withSnapshots(previous.insights, list),
            pendingKinds: [],
          }));
        });
    }
    return () => {
      live = false;
    };
  }, [propertyId, kindsKey, deferredKey, withGrowth]);

  return state;
}
